import { apiConfig, getApiUrl, getAuthHeaders } from '../config/apiConfig';
import { getBaseUrl } from '../config/appSettings';
import { inspectAccessResponse } from './userAccessFlags';
import {
  buildMilestones,
  buildRewardsSummary,
  enrichClaimHistoryWithRewards,
  enrichRewardsForClaim,
  extractRewardProgress,
  normalizeClaimHistory,
  normalizeRewardList,
  normalizeRewardTierList,
  parseMilestoneProgress,
  parseRewardsDashboard,
} from './rewardsData';
import {
  normalizeLoyaltyEvents,
  normalizeLoyaltyHistory,
  normalizeLoyaltyTiers,
  parseLoyaltySummary,
} from './loyaltyData';
import { withUserKycFlags } from '../pages/kyc/kycComplianceData';

// API Service class for handling all API calls
// 
// USAGE EXAMPLES:
// 
// 1. Login (stores token automatically):
//    const response = await apiService.login(email, password);
// 
// 2. Make authenticated API calls (token automatically included):
//    const profile = await apiService.getUserProfile();
//    const dashboard = await apiService.getDashboardData();
// 
// 3. Check authentication status:
//    if (apiService.isAuthenticated()) { ... }
// 
// 4. Get current token:
//    const token = apiService.getAuthToken();
// 
// 5. Logout (clears token):
//    apiService.logout();
const affiliateFundsCache = new Map();
const affiliateFundsInflight = new Map();
const getRequestInflight = new Map();
const postRequestInflight = new Map();

// Network-level failures (server unreachable) → a user-facing message instead of the browser's "Failed to fetch".
const friendlyAgreementError = (error, fallback) =>
  /failed to fetch|networkerror|load failed/i.test(error?.message || '')
    ? 'Unable to reach the server. Please check your connection and try again.'
    : error?.message || fallback;

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const base64ToBlob = (base64, mimeType = EXCEL_MIME) => {
  const cleaned = base64.replace(/^data:.*?;base64,/, '');
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

const resolveDownloadUrl = (downloadUrl) => {
  if (!downloadUrl || typeof downloadUrl !== 'string') return '';
  const trimmed = downloadUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = getBaseUrl().replace(/\/$/, '');
  return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
};

const pickExportFromJson = (json) => {
  const payload = json?.result ?? json?.data ?? json;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

  const fileName = payload.fileName ?? payload.filename ?? 'old-earning-data.xlsx';
  const downloadUrl = payload.downloadUrl ?? payload.downloadURL ?? payload.url;

  if (typeof downloadUrl === 'string' && downloadUrl.trim()) {
    return {
      type: 'url',
      downloadUrl: resolveDownloadUrl(downloadUrl),
      fileName,
    };
  }

  const encoded =
    payload.fileContent ??
    payload.fileBytes ??
    payload.content ??
    payload.base64 ??
    payload.data;

  if (typeof encoded === 'string' && encoded.trim()) {
    return { type: 'blob', blob: base64ToBlob(encoded), fileName };
  }

  return null;
};

export const extractAffiliateFundsList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload?.records)) return payload.records.filter(Boolean);
  return [];
};

export const extractCommissionHistoryList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.commissions)) return payload.commissions.filter(Boolean);
  if (Array.isArray(payload?.commissionHistory)) return payload.commissionHistory.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload?.records)) return payload.records.filter(Boolean);
  return [];
};

export const extractClientsList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.clients)) return payload.clients.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload?.records)) return payload.records.filter(Boolean);
  return [];
};

export const extractClientsListPagination = (response, fallbackPageSize = 10) => {
  const list = extractClientsList(response);
  const emptySummary = {
    totalClients: 0,
    activeClients: 0,
    totalLots: 0,
    totalProfit: 0,
    totalDeposit: 0,
  };
  if (!response?.success) {
    return {
      totalRecords: 0,
      totalPages: 1,
      pageNumber: 1,
      pageSize: fallbackPageSize,
      ...emptySummary,
    };
  }

  const payload = response.data;
  if (!payload || Array.isArray(payload)) {
    return {
      totalRecords: list.length,
      totalPages: 1,
      pageNumber: 1,
      pageSize: fallbackPageSize,
      ...emptySummary,
      totalClients: list.length,
    };
  }

  const pageSize = Number(payload.pageSize ?? payload.page_size ?? fallbackPageSize) || fallbackPageSize;
  const pageNumber = Number(payload.pageNumber ?? payload.page ?? payload.currentPage ?? 1) || 1;
  const totalRecords = Number(
    payload.totalRecords ??
    payload.total ??
    payload.totalCount ??
    payload.count ??
    list.length
  ) || list.length;
  const totalPages = Number(payload.totalPages ?? payload.totalPage) || Math.max(1, Math.ceil(totalRecords / pageSize) || 1);

  return {
    totalRecords,
    totalPages,
    pageNumber,
    pageSize,
    totalClients: Number(payload.totalClients ?? payload.totalClient ?? totalRecords) || 0,
    activeClients: Number(payload.activeClients ?? payload.totalActiveClients ?? 0) || 0,
    totalLots: Number(payload.totalLots ?? 0) || 0,
    totalProfit: Number(payload.totalProfit ?? payload.totalCommission ?? 0) || 0,
    totalDeposit: Number(payload.totalDeposit ?? payload.userTotalDeposit ?? 0) || 0,
  };
};

export const extractChallengeParticipantsList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  return [];
};

export const extractChallengeParticipantsPagination = (response, fallbackPageSize = 10) => {
  const list = extractChallengeParticipantsList(response);
  if (!response?.success) {
    return { totalRecords: 0, totalPages: 1, pageNumber: 1, pageSize: fallbackPageSize };
  }

  const payload = response.data;
  if (!payload || Array.isArray(payload)) {
    return { totalRecords: list.length, totalPages: 1, pageNumber: 1, pageSize: fallbackPageSize };
  }

  const pageSize = Number(payload.pageSize ?? fallbackPageSize) || fallbackPageSize;
  const pageNumber = Number(payload.pageNumber ?? 1) || 1;
  const totalRecords = Number(payload.totalRecords ?? list.length) || list.length;
  const totalPages = Number(payload.totalPages) || Math.max(1, Math.ceil(totalRecords / pageSize) || 1);

  return { totalRecords, totalPages, pageNumber, pageSize };
};

export const extractAffiliateFundsPagination = (response, fallbackPageSize = 10) => {
  const list = extractAffiliateFundsList(response);
  if (!response?.success) {
    return { totalRecords: 0, totalPages: 1, pageNumber: 1, pageSize: fallbackPageSize };
  }

  const payload = response.data;
  if (!payload || Array.isArray(payload)) {
    return {
      totalRecords: list.length,
      totalPages: 1,
      pageNumber: 1,
      pageSize: fallbackPageSize,
    };
  }

  const pageSize = Number(payload.pageSize ?? payload.page_size ?? fallbackPageSize) || fallbackPageSize;
  const pageNumber = Number(payload.pageNumber ?? payload.page ?? payload.currentPage ?? 1) || 1;
  const totalRecords = Number(
    payload.totalRecords ??
    payload.total ??
    payload.totalCount ??
    payload.count ??
    list.length
  ) || list.length;
  const totalPages = Number(payload.totalPages ?? payload.totalPage) || Math.max(1, Math.ceil(totalRecords / pageSize));

  return { totalRecords, totalPages, pageNumber, pageSize };
};

const pickNumber = (source, ...keys) => {
  for (const key of keys) {
    const val = source?.[key];
    if (val !== undefined && val !== null && val !== '') {
      const num = Number(val);
      if (Number.isFinite(num)) return num;
    }
  }
  return null;
};

const pickSummarySource = (response) => {
  const payload = response?.data;
  if (!payload || Array.isArray(payload)) return null;
  if (payload.result && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
    return payload.result;
  }
  return payload;
};

export const extractAffiliateFundsSummary = (response) => {
  const empty = {
    hasData: false,
    hasCounts: false,
    total: 0,
    completedAmount: 0,
    completedCount: 0,
    pendingAmount: 0,
    pendingCount: 0,
    failedAmount: 0,
    failedCount: 0,
  };

  if (!response?.success) return empty;

  const source = pickSummarySource(response);
  if (!source) return empty;

  const total = pickNumber(
    source,
    'totalPayout',
    'totalPayouts',
    'totalWithdrawal',
    'totalAmount',
    'total'
  );
  const completedAmount = pickNumber(
    source,
    'completedPayout',
    'completedAmount',
    'paidAmount',
    'paidPayout'
  );
  const pendingAmount = pickNumber(
    source,
    'pendingPayouts',
    'pendingPayout',
    'pendingAmount'
  );
  const failedAmount = pickNumber(
    source,
    'rejectPayouts',
    'rejectPayout',
    'rejectAmount',
    'failedAmount',
    'rejectedAmount'
  );
  const completedCount = pickNumber(
    source,
    'completedCount',
    'completedPayoutCount',
    'completedTransactions',
    'paidCount'
  );
  const pendingCount = pickNumber(
    source,
    'pendingCount',
    'pendingPayoutCount',
    'pendingTransactions'
  );
  const failedCount = pickNumber(
    source,
    'rejectCount',
    'rejectPayoutCount',
    'failedCount',
    'rejectTransactions',
    'rejectedCount'
  );

  const hasAmountData = [total, completedAmount, pendingAmount, failedAmount].some((v) => v !== null);
  const hasCountData = [completedCount, pendingCount, failedCount].some((v) => v !== null);

  if (!hasAmountData && !hasCountData) return empty;

  return {
    hasData: true,
    hasCounts: hasCountData,
    total: total ?? 0,
    completedAmount: completedAmount ?? 0,
    completedCount: completedCount ?? 0,
    pendingAmount: pendingAmount ?? 0,
    pendingCount: pendingCount ?? 0,
    failedAmount: failedAmount ?? 0,
    failedCount: failedCount ?? 0,
  };
};

export const extractCommissionHistoryPagination = (response, fallbackPageSize = 10) => {
  const emptySummary = { totalClients: 0, totalActiveClients: 0, totalLots: 0, totalProfit: 0 };
  const list = extractCommissionHistoryList(response);
  if (!response?.success) {
    return { totalRecords: 0, totalPages: 1, pageNumber: 1, pageSize: fallbackPageSize, ...emptySummary };
  }

  const payload = response.data;
  if (!payload || Array.isArray(payload)) {
    return {
      totalRecords: list.length,
      totalPages: 1,
      pageNumber: 1,
      pageSize: fallbackPageSize,
      ...emptySummary,
    };
  }

  const pageSize = Number(payload.pageSize ?? payload.page_size ?? fallbackPageSize) || fallbackPageSize;
  const pageNumber = Number(payload.pageNumber ?? payload.page ?? payload.currentPage ?? 1) || 1;
  const totalRecords = Number(
    payload.totalRecords ??
    payload.total ??
    payload.totalCount ??
    payload.count ??
    list.length
  ) || list.length;
  const totalPages = Number(payload.totalPages ?? payload.totalPage) || Math.max(1, Math.ceil(totalRecords / pageSize));

  return {
    totalRecords,
    totalPages,
    pageNumber,
    pageSize,
    totalClients: Number(payload.totalClients ?? payload.totalClient ?? 0) || 0,
    totalActiveClients: Number(payload.totalActiveClients ?? 0) || 0,
    totalLots: Number(payload.totalLots ?? 0) || 0,
    totalProfit: Number(payload.totalProfit ?? payload.totalCommission ?? 0) || 0,
  };
};

class ApiService {
  // Generic request method
  async request(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    const config = {
      method: 'GET',
      headers: getAuthHeaders(),
      ...options
    };

    const method = (config.method || 'GET').toUpperCase();
    const dedupeKey = method === 'GET' ? `GET:${endpoint}` : null;

    const executeRequest = async () => {
      try {
        const response = await fetch(url, config);

        if (!response.ok) {
          // Handle 401 Unauthorized - token expired or invalid
          if (response.status === 401) {
            this.handleTokenExpiration();
            throw new Error('Authentication failed. Please login again.');
          }

          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        // Error handling
        throw error;
      }
    };

    if (!dedupeKey) {
      return executeRequest();
    }

    if (getRequestInflight.has(dedupeKey)) {
      return getRequestInflight.get(dedupeKey);
    }

    const requestPromise = executeRequest().finally(() => {
      getRequestInflight.delete(dedupeKey);
    });

    getRequestInflight.set(dedupeKey, requestPromise);
    return requestPromise;
  }

  formatApiError(response, apiName) {
    const top = String(
      response?.response?.responseMessage ?? response?.responseMessage ?? ''
    ).trim();
    const detail = typeof response?.result === 'string' ? response.result.trim() : '';
    if (detail && (!top || /^error$/i.test(top))) return detail;
    if (top && detail && top !== detail) return `${top}: ${detail}`;
    return top || `${apiName} failed`;
  }

  // Common response handler for all APIs
  handleApiResponse(response, apiName) {
    // Suspended → refresh access flags now; inactive → force logout
    inspectAccessResponse(response);

    // Handle different possible response formats
    
    // Format 1: Standard format with response.response.responseCode
    if (response.response && response.response.responseCode === 0) {
      const result = {
        success: true,
        message: response.response.responseMessage || `${apiName} successful`,
        data: response.result
      };
      // API response processed (Format 1)
      return result;
    }
    
    // Format 2: Direct response with responseCode
    if (response.responseCode === 0) {
      const result = {
        success: true,
        message: response.responseMessage || `${apiName} successful`,
        data: response.result || response.data
      };
      // API response processed (Format 2)
      return result;
    }
    
    // Error case - responseCode is not 0 (error response)
    if (response.response && response.response.responseCode !== 0) {
      const errorMessage = this.formatApiError(response, apiName);
      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        data: response.result ?? null,
      };
    }
    
    // Error case - direct responseCode is not 0
    if (response.responseCode && response.responseCode !== 0) {
      const errorMessage = this.formatApiError(response, apiName);
      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        data: response.result ?? null,
      };
    }
    
    // Format 3: Simple success response (no responseCode, just data)
    if (response.result || response.data) {
      const result = {
        success: true,
        message: `${apiName} successful`,
        data: response.result || response.data
      };
      // API response processed (Format 3)
      return result;
    }
    
    // Format 4: Check if response has any data at all
    if (response && typeof response === 'object' && Object.keys(response).length > 0) {
      const result = {
        success: true,
        message: `${apiName} successful`,
        data: response
      };
      // API response processed (Format 4)
      return result;
    }
    
    // Final error case - no valid data found
    const errorMessage = response.response?.responseMessage || response.responseMessage || `${apiName} failed - no data received`;
    return {
      success: false,
      error: errorMessage,
      message: errorMessage
    };
  }

  // Login API
  // Returns { success:false, twoFactorRequired:true, setupRequired, challengeToken } when the partner must
  // complete the authenticator step (backend code 3 / TWO_FACTOR_REQUIRED). Nothing is persisted in that case.
  async login(affiliateCode, password) {
    const payload = {
      affiliateCode: affiliateCode.trim(),
      password,
    };

    const response = await this.request(apiConfig.endpoints.login, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    const result = this.handleApiResponse(response, 'Login');

    if (!result.success && result.data && result.data.errorCode === 'TWO_FACTOR_REQUIRED') {
      return {
        success: false,
        twoFactorRequired: true,
        setupRequired: result.data.setupRequired === true,
        challengeToken: result.data.challengeToken,
        errorCode: 'TWO_FACTOR_REQUIRED',
        message: result.message,
        error: result.error,
      };
    }

    return this.persistLoginSession(result, affiliateCode);
  }

  // Persists a successful login (normal or after the two-factor step). No-op unless result.success.
  persistLoginSession(result, affiliateCode) {
    // Store user data if available
    if (result.success && result.data) {
      const code = (affiliateCode || result.data.affiliateCode || '').trim();
      if (code) localStorage.setItem('affiliateCode', code);
      if (result.data.email) {
        localStorage.setItem('userEmail', result.data.email);
      }
      localStorage.setItem('isLoggedIn', 'true');
      
      // Store userName if available (firstName + lastName or userName)
      if (result.data.firstName && result.data.lastName) {
        localStorage.setItem('userName', `${result.data.firstName} ${result.data.lastName}`);
      } else if (result.data.userName) {
        localStorage.setItem('userName', result.data.userName);
      } else if (result.data.name) {
        localStorage.setItem('userName', result.data.name);
      }
      
      // Store authentication token if available
      if (result.data.token) {
        localStorage.setItem('authToken', result.data.token);
      } else if (result.data.accessToken) {
        localStorage.setItem('authToken', result.data.accessToken);
      } else if (result.data.jwtToken) {
        localStorage.setItem('authToken', result.data.jwtToken);
      }
      
      // Store user data and permissions
      if (result.data.userPermissions) {
        localStorage.setItem('userPermissions', JSON.stringify(result.data.userPermissions));
      } else {
        // Don't set default permissions - let the system handle empty permissions
        localStorage.setItem('userPermissions', JSON.stringify([]));
      }
      
      // Store email verification status
      localStorage.setItem('isEmailVerified', result.data.isEmailVerified === true ? 'true' : 'false');

      // Normalize KYC flags (isVerified / verificationStatus from login)
      result.data = withUserKycFlags(result.data);

      // Store complete user data
      localStorage.setItem('userData', JSON.stringify(result.data));
    }
    
    return result;
  }

  // ----- Two-factor (authenticator app) login steps. All use the short-lived challengeToken from login. -----
  // Nothing is persisted by these three calls; the caller persists via persistLoginSession() after the final step.

  // Step: get a fresh secret + otpauth URI for first-time setup. data: { secret, otpAuthUri, issuer, accountLabel }
  async twoFactorSetup(challengeToken) {
    const response = await this.request(apiConfig.endpoints.twoFactorSetup, {
      method: 'POST',
      body: JSON.stringify({ challengeToken })
    });
    return this.handleApiResponse(response, 'Two-Factor Setup');
  }

  // Step: confirm the first code. data: { user, recoveryCodes } (user includes the session token)
  async twoFactorConfirmSetup(challengeToken, code) {
    const response = await this.request(apiConfig.endpoints.twoFactorConfirmSetup, {
      method: 'POST',
      body: JSON.stringify({ challengeToken, code })
    });
    return this.handleApiResponse(response, 'Two-Factor Confirm Setup');
  }

  // Step: verify a TOTP code or a one-time recovery code. data: the same user object a normal login returns.
  async twoFactorVerify(challengeToken, { code, recoveryCode } = {}) {
    const payload = { challengeToken };
    if (recoveryCode) payload.recoveryCode = recoveryCode;
    else payload.code = code;
    const response = await this.request(apiConfig.endpoints.twoFactorVerify, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return this.handleApiResponse(response, 'Two-Factor Verify');
  }

  /** Live access flags for the current partner (always from DB). */
  async getAccessFlags() {
    const response = await this.request('/User/GetAccessFlags', { method: 'GET', cache: 'no-store' });
    return this.handleApiResponse(response, 'Get Access Flags');
  }

  // Register API — POST /account/createuser
  async register(userData) {
    const requestBody = {
      fullName:
        userData.fullName ||
        [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim(),
      emailAddress: userData.emailAddress || userData.email,
      password: userData.password,
      confirmPassword: userData.confirmPassword,
      countryId: Number(userData.countryId) || 0,
      countryCode: userData.countryCode || '',
      mobileNumber: userData.mobileNumber || '',
      state: userData.state || '',
      city: userData.city || '',
      isTermsCondition: userData.isTermsCondition !== false,
      ...(userData.referralCode ? { referralCode: userData.referralCode } : {}),
    };

    const response = await this.request(apiConfig.endpoints.register, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });

    return this.handleApiResponse(response, 'Registration');
  }

  // Get Countries API
  async getCountries() {
    const response = await this.request(apiConfig.endpoints.getCountries, {
      method: 'GET'
    });

    const result = this.handleApiResponse(response, 'Countries');
    
    // Ensure data is always an array for countries
    return {
      ...result,
      data: result.data || []
    };
  }

  // Forgot Password API
  async forgotPassword(email) {
    const payload = { email };
    const response = await this.request(apiConfig.endpoints.forgotPassword, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Forgot Password');
  }

  // Reset Password API
  async resetPassword(token, newPassword) {
    const payload = {
      token,
      newPassword
    };
    const response = await this.request(apiConfig.endpoints.resetPassword, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Reset Password');
  }

  // Get User Profile API
  async getUserProfile() {
    const response = await this.request(apiConfig.endpoints.getUserProfile, {
      method: 'GET'
    });

    const result = this.handleApiResponse(response, 'Get User Profile');
    if (result.success && result.data) {
      result.data = withUserKycFlags(result.data);
    }
    return result;
  }

  // Update User Profile API
  async updateUserProfile(profileData) {
    // Only send the fields that are allowed to be updated
    const requestBody = {
      state: profileData.state || '',
      city: profileData.city || '',
      zipCode: profileData.zipCode || '',
      address: profileData.address || '',
      lastCompanyName: profileData.lastCompanyName || '',
      totalClients: profileData.totalClients || '',
      totalTradingExp: profileData.totalTradingExp || '',
      dateOfBirth: profileData.dateOfBirth || '',
    };

    const response = await this.request(apiConfig.endpoints.updateUserProfile, {
      method: 'PUT',
      body: JSON.stringify(requestBody)
    });

    return this.handleApiResponse(response, 'Update User Profile');
  }

  // Get Dashboard Data API
  async getDashboardData() {
    const response = await this.request(apiConfig.endpoints.getDashboardData, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Dashboard Data');
  }

  // Get Dashboard Widgets API
  async getDashboardWidgets() {
    try {
      const response = await this.request(apiConfig.endpoints.getDashboardWidgets, {
        method: 'GET'
      });

      const result = this.handleApiResponse(response, 'Get Dashboard Widgets');
      return result;
    } catch (error) {
      // Error handling
      throw error;
    }
  }

  /**
   * GET /Dashboard/GetNextLotRebatePayment
   * Header widget: total hold + period countdown. Success: responseCode === 0
   */
  async getNextLotRebatePayment() {
    try {
      const response = await this.request(apiConfig.endpoints.getNextLotRebatePayment, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Next Lot Rebate Payment');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load next lot rebate',
        data: null,
      };
    }
  }

  /**
   * GET /Dashboard/GetDefaultAffiliatePromotion
   * Fallback: /AffiliatePromotions/GetDefaultAffiliatePromotion
   * Success: responseCode === 0
   */
  async getDefaultAffiliatePromotion() {
    const tryEndpoint = async (endpoint) => {
      const response = await this.request(endpoint, { method: 'GET' });
      return this.handleApiResponse(response, 'Get Default Affiliate Promotion');
    };

    try {
      const primary = await tryEndpoint(apiConfig.endpoints.getDefaultAffiliatePromotion);
      if (primary.success && primary.data) return primary;

      const alt = await tryEndpoint(apiConfig.endpoints.getDefaultAffiliatePromotionAlt);
      if (alt.success && alt.data) return alt;

      return primary.success ? primary : alt;
    } catch (error) {
      try {
        return await tryEndpoint(apiConfig.endpoints.getDefaultAffiliatePromotionAlt);
      } catch (altError) {
        return {
          success: false,
          error: altError.message || error.message || 'Failed to load default promotion',
          data: null,
        };
      }
    }
  }

  // Get Earning Overview API (Dashboard)
  async getEarningOverview() {
    const response = await this.request(apiConfig.endpoints.getEarningOverview, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Earning Overview');
  }

  // Get Performance Overview API (Dashboard)
  // Date-range filtered dashboard figures (Dashboard 2). Dates are yyyy-MM-dd.
  async getDashboardRangeSummary(fromDate, toDate) {
    const query = new URLSearchParams({ fromDate, toDate }).toString();
    const response = await this.request(`${apiConfig.endpoints.getDashboardRangeSummary}?${query}`, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Dashboard Range Summary');
  }

  async getPerformanceOverview() {
    const response = await this.request(apiConfig.endpoints.getPerformanceOverview, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Performance Overview');
  }


  // Get Reports API
  async getReports() {
    const response = await this.request(apiConfig.endpoints.getReports, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Reports');
  }

  // Get Report Summary API
  async getReportSummary() {
    const response = await this.request(apiConfig.endpoints.getReportSummary, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Summary');
  }

  // Get Report Revenue Monthwise API
  async getReportRevenueMonthwise() {
    const response = await this.request(apiConfig.endpoints.getReportRevenueMonthwise, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Revenue Monthwise');
  }

  // Get Report Commission Monthwise API
  async getReportCommissionMonthwise() {
    const response = await this.request(apiConfig.endpoints.getReportCommissionMonthwise, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Commission Monthwise');
  }

  // Get Report Symbol Breakdown API
  async getReportSymbolBreakdown() {
    const response = await this.request(apiConfig.endpoints.getReportSymbolBreakdown, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Symbol Breakdown');
  }

  // Get Report Affiliate List API
  async getReportAffiliateList() {
    const response = await this.request(apiConfig.endpoints.getReportAffiliateList, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Affiliate List');
  }

  // Get Report Clients List API
  async getReportClientsList(params = {}) {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters if they exist
    if (params.pageNumber) {
      queryParams.append('PageNumber', params.pageNumber);
    }
    if (params.pageSize) {
      queryParams.append('PageSize', params.pageSize);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString 
      ? `${apiConfig.endpoints.getReportClientsList}?${queryString}`
      : apiConfig.endpoints.getReportClientsList;

    const response = await this.request(endpoint, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Report Clients List');
  }

  // Get Performance Widgets API
  async getPerformanceWidgets() {
    const response = await this.request(apiConfig.endpoints.getPerformanceWidgets, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Performance Widgets');
  }

  async getMonthlyPerformanceTrend() {
    const response = await this.request(apiConfig.endpoints.getMonthlyPerformanceTrend, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Monthly Performance Trend');
  }

  async getPerformanceSummary() {
    const response = await this.request(apiConfig.endpoints.getPerformanceSummary, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Performance Summary');
  }

  // Get Commissions API
  async getCommissions(params = {}) {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    // Always add pagination parameters
    queryParams.append('PageNumber', params.pageNumber || 1);
    queryParams.append('PageSize', params.pageSize || 10);
    
    // Add other parameters if they exist
    if (params.fromDate && params.fromDate.trim() !== '') {
      queryParams.append('FromDate', params.fromDate);
    }
    if (params.toDate && params.toDate.trim() !== '') {
      queryParams.append('ToDate', params.toDate);
    }
    if (params.affiliateLevel && params.affiliateLevel.trim() !== '') {
      queryParams.append('AffiliateLevel', params.affiliateLevel);
    }
    if (params.clientUserId && params.clientUserId.trim() !== '') {
      queryParams.append('ClientUserId', params.clientUserId);
    }
    if (params.searchTerm && params.searchTerm.trim() !== '') {
      queryParams.append('SearchTerm', params.searchTerm);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString 
      ? `${apiConfig.endpoints.getCommissions}?${queryString}`
      : apiConfig.endpoints.getCommissions;

    const response = await this.request(endpoint, {
      method: 'GET'
    });

    // Handle the specific response format for commissions
    if (response.response && response.response.responseCode === 0) {
      // Success case - responseCode 0 means success
      const result = {
        success: true,
        message: response.response.responseMessage || 'Get Commissions successful',
        data: response.result || response.data || response
      };
      return result;
    } else {
      // Error case - responseCode 1 or any other code means error
      const errorMessage = response.response?.responseMessage || 'Get Commissions failed';
      throw new Error(errorMessage);
    }
  }

  // Get Commission History API (Earning List)
  async getCommissionHistory(params = {}) {
    const payload = {
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      // Empty string breaks DateTime? binding on the API (whole body becomes null), so send null.
      fromDate: params.fromDate || null,
      toDate: params.toDate || null,
      searchTerm: params.searchTerm ?? '',
      clientCode: params.clientCode ?? '',
      accountCode: params.accountCode ?? '',
    };

    const inflightKey = [
      apiConfig.endpoints.getCommissionHistory,
      payload.pageNumber,
      payload.pageSize,
      payload.fromDate,
      payload.toDate,
      payload.searchTerm,
      payload.clientCode,
      payload.accountCode,
    ].join('|');
    if (postRequestInflight.has(inflightKey)) {
      return postRequestInflight.get(inflightKey);
    }

    const requestPromise = this.request(apiConfig.endpoints.getCommissionHistory, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((response) => this.handleApiResponse(response, 'Get Commission History'))
      .finally(() => {
        postRequestInflight.delete(inflightKey);
      });

    postRequestInflight.set(inflightKey, requestPromise);
    return requestPromise;
  }

  // Get Old Data History API (Earning List — legacy data)
  async getOldCommissionHistory(params = {}) {
    const payload = {
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      // Empty string breaks DateTime? binding on the API (whole body becomes null), so send null.
      fromDate: params.fromDate || null,
      toDate: params.toDate || null,
      searchTerm: params.searchTerm ?? '',
      clientCode: params.clientCode ?? '',
      accountCode: params.accountCode ?? '',
    };

    const inflightKey = [
      apiConfig.endpoints.getOldDataHistory,
      payload.pageNumber,
      payload.pageSize,
      payload.fromDate,
      payload.toDate,
      payload.searchTerm,
      payload.clientCode,
      payload.accountCode,
    ].join('|');
    if (postRequestInflight.has(inflightKey)) {
      return postRequestInflight.get(inflightKey);
    }

    const requestPromise = this.request(apiConfig.endpoints.getOldDataHistory, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((response) => this.handleApiResponse(response, 'Get Old Data History'))
      .finally(() => {
        postRequestInflight.delete(inflightKey);
      });

    postRequestInflight.set(inflightKey, requestPromise);
    return requestPromise;
  }

  // Export Old Data History to Excel
  async exportOldDataHistoryExcel(params = {}) {
    const payload = {
      pageNumber: params.pageNumber ?? 0,
      pageSize: params.pageSize ?? 0,
      // Empty string breaks DateTime? binding on the API (whole body becomes null), so send null.
      fromDate: params.fromDate || null,
      toDate: params.toDate || null,
      searchTerm: params.searchTerm ?? '',
      clientCode: params.clientCode ?? '',
      accountCode: params.accountCode ?? '',
    };

    const url = getApiUrl(apiConfig.endpoints.getOldDataHistoryExportExcel);
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      this.handleTokenExpiration();
      throw new Error('Authentication failed. Please login again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData.response?.responseMessage ||
        errorData.message ||
        `Export failed (${response.status})`;
      throw new Error(message);
    }

    const json = await response.json();
    if (json.response?.responseCode !== undefined && json.response.responseCode !== 0) {
      throw new Error(json.response?.responseMessage || 'Export failed');
    }
    if (json.responseCode !== undefined && json.responseCode !== 0) {
      throw new Error(json.responseMessage || 'Export failed');
    }

    const exportFile = pickExportFromJson(json);
    if (!exportFile) {
      throw new Error('No download URL in export response');
    }

    if (exportFile.type === 'url') {
      return { downloadUrl: exportFile.downloadUrl, fileName: exportFile.fileName };
    }

    return { blob: exportFile.blob, fileName: exportFile.fileName };
  }

  // Get Commission Widgets API
  async getCommissionWidgets() {
    const response = await this.request(apiConfig.endpoints.getCommissionWidgets, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Commission Widgets');
  }

  // Get Commission Summary API (legacy)
  async getCommissionSummary() {
    const response = await this.request(apiConfig.endpoints.getCommissionSummary, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Commission Summary');
  }

  // Get Earning Dashboard API
  async getEarningDashboard() {
    const response = await this.request(apiConfig.endpoints.getEarningDashboard, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Earning Dashboard');
  }

  // Get Clients List API
  async getClientsList(params = {}) {
    const payload = {
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      searchTerm: params.searchTerm ?? '',
      loginId: params.loginId ?? '',
      type: params.type ?? '',
      sortBy: params.sortBy ?? '',
      sortOrder: params.sortOrder ?? '',
    };

    const inflightKey = [
      apiConfig.endpoints.getClientsList,
      payload.pageNumber,
      payload.pageSize,
      payload.searchTerm,
      payload.loginId,
      payload.type,
      payload.sortBy,
      payload.sortOrder,
    ].join('|');
    if (postRequestInflight.has(inflightKey)) {
      return postRequestInflight.get(inflightKey);
    }

    const requestPromise = this.request(apiConfig.endpoints.getClientsList, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((response) => this.handleApiResponse(response, 'Get Clients List'))
      .finally(() => {
        postRequestInflight.delete(inflightKey);
      });

    postRequestInflight.set(inflightKey, requestPromise);
    return requestPromise;
  }

  /** POST /Client/GetClientReport - permitted fields of the logged-in partner's own clients. */
  async getClientReport(filters = {}) {
    const response = await this.request(apiConfig.endpoints.getClientReport, {
      method: 'POST',
      body: JSON.stringify(filters),
    });
    return this.handleApiResponse(response, 'Get Client Report');
  }

  /** POST /Client/DownloadClientReportExcel - returns the .xlsx as a Blob (streamed, never stored on the server). */
  async downloadClientReportExcel(filters = {}) {
    const response = await fetch(getApiUrl(apiConfig.endpoints.downloadClientReportExcel), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    });

    if (response.status === 401) {
      this.handleTokenExpiration();
      throw new Error('Authentication failed. Please login again.');
    }
    if (!response.ok) {
      throw new Error(`Excel export failed (${response.status})`);
    }
    return response.blob();
  }

  // Get Affiliates API
  async getAffiliates() {
    const response = await this.request(apiConfig.endpoints.getAffiliates, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Affiliates');
  }

  /**
   * GET /Affiliate/GetSubPartnersList
   * Sub partners where AffiliateParentId = current user.
   */
  async getSubPartnersList() {
    try {
      const response = await this.request(apiConfig.endpoints.getSubPartnersList, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Sub Partners List');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load sub partners',
        data: null,
      };
    }
  }

  clearAffiliateFundsCache() {
    affiliateFundsCache.clear();
    affiliateFundsInflight.clear();
  }

  // Get Affiliate Funds API (cached per query params)
  async getAffiliateFunds(params = {}, options = {}) {
    const { force = false } = options;

    const queryParams = new URLSearchParams();

    if (params.pageNumber) {
      queryParams.append('pageNumber', params.pageNumber);
    }
    if (params.pageSize) {
      queryParams.append('pageSize', params.pageSize);
    }
    if (params.searchTerm) {
      queryParams.append('searchTerm', params.searchTerm);
    }
    if (params.fromDate) {
      queryParams.append('fromDate', params.fromDate);
    }
    if (params.toDate) {
      queryParams.append('toDate', params.toDate);
    }
    if (params.status && params.status !== 'all') {
      queryParams.append('status', params.status);
    }

    const queryString = queryParams.toString();
    const cacheKey = queryString || 'default';

    if (!force && affiliateFundsCache.has(cacheKey)) {
      return affiliateFundsCache.get(cacheKey);
    }

    if (!force && affiliateFundsInflight.has(cacheKey)) {
      return affiliateFundsInflight.get(cacheKey);
    }

    const url = queryString ? `${apiConfig.endpoints.getAffiliateFunds}?${queryString}` : apiConfig.endpoints.getAffiliateFunds;

    const requestPromise = (async () => {
      try {
        const response = await this.request(url, { method: 'GET' });
        const result = this.handleApiResponse(response, 'Get Affiliate Funds');
        affiliateFundsCache.set(cacheKey, result);
        return result;
      } finally {
        affiliateFundsInflight.delete(cacheKey);
      }
    })();

    affiliateFundsInflight.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // Get Transaction Widget API
  async getTransactionWidget() {
    const response = await this.request(apiConfig.endpoints.getTransactionWidget, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Transaction Widget');
  }

  // Get Wallets API
  async getWallets() {
    const response = await this.request(apiConfig.endpoints.getWallets, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Wallets');
  }

  // Get Sub Payment Methods by wallet
  async getSubPaymentMethods(walletId) {
    const query = new URLSearchParams({ walletId: String(walletId) });
    const endpoint = `${apiConfig.endpoints.getSubPaymentMethods}?${query.toString()}`;
    const response = await this.request(endpoint, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Sub Payment Methods');
  }

  // Partner payment / withdrawal account details
  async getPartnerPaymentDetails() {
    const response = await this.request(apiConfig.endpoints.getPartnerPaymentDetails, {
      method: 'GET',
    });

    return this.handleApiResponse(response, 'Get Partner Payment Details');
  }

  async createPartnerPaymentDetails(data) {
    const response = await this.request(apiConfig.endpoints.createPartnerPaymentDetails, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });

    return this.handleApiResponse(response, 'Create Partner Payment Details');
  }

  async updatePartnerPaymentDetails(data) {
    const response = await this.request(apiConfig.endpoints.updatePartnerPaymentDetails, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });

    return this.handleApiResponse(response, 'Update Partner Payment Details');
  }

  async deletePartnerPaymentDetails(partnerPaymentDetailId) {
    const response = await this.request(apiConfig.endpoints.deletePartnerPaymentDetails, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ partnerPaymentDetailId }),
    });

    return this.handleApiResponse(response, 'Delete Partner Payment Details');
  }

  // Create Withdrawal Request API
  async createWithdrawalRequest(data) {
    const response = await this.request(apiConfig.endpoints.createWithdrawalRequest, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });

    return this.handleApiResponse(response, 'Create Withdrawal Request');
  }

  // Cancel Withdrawal Request API
  async cancelWithdrawalRequest(affiliateFundId) {
    const response = await this.request(apiConfig.endpoints.cancelWithdrawalRequest, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ affiliateFundId })
    });

    return this.handleApiResponse(response, 'Cancel Withdrawal Request');
  }

  // Logout method
  logout() {
    this.clearAffiliateFundsCache();
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('affiliateCode');
    localStorage.removeItem('isLoggedIn');
    window.location.href = '/login';
  }

  // Check if user is authenticated
  isAuthenticated() {
    return localStorage.getItem('authToken') && localStorage.getItem('isLoggedIn') === 'true';
  }

  // Get current auth token
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  // Check if token exists and is valid
  hasValidToken() {
    const token = localStorage.getItem('authToken');
    const loginStatus = localStorage.getItem('isLoggedIn');
    return token && token.trim() !== '' && loginStatus === 'true';
  }

  // Validate token with server
  async validateToken() {
    try {
      const response = await this.request('/auth/validate-token', {
        method: 'GET'
      });
      return response.success;
    } catch (error) {
      // Error handling
      return false;
    }
  }

  // Get User Levels
  async getUserLevels() {
    try {
      const response = await this.request('/Affiliate/GetUserLevels');
      const result = this.handleApiResponse(response, 'Get User Levels');
      return result;
    } catch (error) {
      // Error handling
      throw error;
    }
  }

  /**
   * GET /Affiliate/GetAccountTierProgress
   * Single payload for Account Tier → My Progress (replaces GetUserLevels on that page).
   */
  async getAccountTierProgress() {
    try {
      const response = await this.request(apiConfig.endpoints.getAccountTierProgress, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Account Tier Progress');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load account tier progress',
        data: null,
      };
    }
  }

  // Get Affiliate Types
  async getAffiliateTypes() {
    try {
      const response = await this.request('/Affiliate/GetAffiliateTypes');
      const result = this.handleApiResponse(response, 'Get Affiliate Types');
      return result;
    } catch (error) {
      // Error handling
      throw error;
    }
  }

  // Get Partners Levels
  async getPartnersLevels() {
    try {
      const response = await this.request('/Affiliate/GetPartnersLevels');
      const result = this.handleApiResponse(response, 'Get Partners Levels');
      return result;
    } catch (error) {
      // Error handling
      throw error;
    }
  }

  /**
   * GET /Affiliate/GetReferSubPartners
   * Refer-a-friend summary + list. Success: responseCode === 0
   */
  async getReferSubPartners() {
    try {
      const response = await this.request(apiConfig.endpoints.getReferSubPartners, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Refer Sub Partners');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load referrals',
        data: null,
      };
    }
  }

  /**
   * PUT /Affiliate/UpdateReferSubPartnerCommission
   * Body: { affiliateReferSubPartnerId, affiliateSubPartnerId, percentage }
   */
  async updateReferSubPartnerCommission({ affiliateReferSubPartnerId, affiliateSubPartnerId, percentage }) {
    try {
      const pct = Number(percentage);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return { success: false, error: 'Enter a percentage between 0 and 100', data: null };
      }

      const response = await this.request(apiConfig.endpoints.updateReferSubPartnerCommission, {
        method: 'PUT',
        body: JSON.stringify({
          affiliateReferSubPartnerId: Number(affiliateReferSubPartnerId) || 0,
          affiliateSubPartnerId: Number(affiliateSubPartnerId) || 0,
          percentage: pct,
        }),
      });
      return this.handleApiResponse(response, 'Update Refer Sub Partner Commission');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update commission',
        data: null,
      };
    }
  }

  /**
   * GET /Affiliate/GetAffiliateSubCommissions
   * Success: responseCode === 0
   */
  async getAffiliateSubCommissions() {
    try {
      const response = await this.request(apiConfig.endpoints.getAffiliateSubCommissions, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Affiliate Sub Commissions');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load commission options',
        data: null,
      };
    }
  }

  /**
   * POST /Affiliate/CreateAffiliateSubCommission
   * Body: { percentage, isActive }
   */
  async createAffiliateSubCommission({ percentage, isActive = true }) {
    try {
      const pct = Number(percentage);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return { success: false, error: 'Enter a percentage between 0 and 100', data: null };
      }

      const response = await this.request(apiConfig.endpoints.createAffiliateSubCommission, {
        method: 'POST',
        body: JSON.stringify({ percentage: pct, isActive: Boolean(isActive) }),
      });
      return this.handleApiResponse(response, 'Create Affiliate Sub Commission');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create sub commission',
        data: null,
      };
    }
  }

  /**
   * PUT /Affiliate/UpdateAffiliateSubCommission
   * Body: { affiliateSubCommissionId, percentage, isActive }
   */
  async updateAffiliateSubCommission({ affiliateSubCommissionId, percentage, isActive = true }) {
    try {
      const id = Number(affiliateSubCommissionId);
      const pct = Number(percentage);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid sub commission id', data: null };
      }
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return { success: false, error: 'Enter a percentage between 0 and 100', data: null };
      }

      const response = await this.request(apiConfig.endpoints.updateAffiliateSubCommission, {
        method: 'PUT',
        body: JSON.stringify({
          affiliateSubCommissionId: id,
          percentage: pct,
          isActive: Boolean(isActive),
        }),
      });
      return this.handleApiResponse(response, 'Update Affiliate Sub Commission');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update sub commission',
        data: null,
      };
    }
  }

  /**
   * DELETE /Affiliate/DeleteAffiliateSubCommission?affiliateSubCommissionId=
   */
  async deleteAffiliateSubCommission(affiliateSubCommissionId) {
    try {
      const id = Number(affiliateSubCommissionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid sub commission id', data: null };
      }

      const response = await this.request(
        `${apiConfig.endpoints.deleteAffiliateSubCommission}?affiliateSubCommissionId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Affiliate Sub Commission');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete sub commission',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetCoursesList
   */
  async getEducationCoursesList({
    pageNumber = 1,
    pageSize = 10,
    searchTerm = '',
    category = '',
    level = '',
    status = '',
    sortBy = '',
    sortOrder = 'desc',
  } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationCoursesList, {
        method: 'POST',
        body: JSON.stringify({
          pageNumber,
          pageSize,
          searchTerm: searchTerm || null,
          category: category || null,
          level: level || null,
          status: status || null,
          sortBy: sortBy || null,
          sortOrder: sortOrder || 'desc',
        }),
      });
      return this.handleApiResponse(response, 'Get Education Courses');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load courses',
        data: null,
      };
    }
  }

  /**
   * GET /Education/GetCourseById?courseId=
   */
  async getEducationCourseById(courseId) {
    try {
      const id = Number(courseId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid course id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.getEducationCourseById}?courseId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load course',
        data: null,
      };
    }
  }

  /**
   * POST /Education/CreateCourse (multipart/form-data)
   * fields + optional Thumbnail file
   */
  async createEducationCourse(fields, thumbnailFile = null) {
    try {
      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        if (value == null) return;
        formData.append(key, String(value));
      });
      if (thumbnailFile) {
        formData.append('Thumbnail', thumbnailFile, thumbnailFile.name || 'thumbnail.jpg');
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(apiConfig.endpoints.createEducationCourse), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.status === 401) {
        this.handleTokenExpiration();
        return { success: false, error: 'Authentication failed. Please login again.', data: null };
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.result || data?.responseMessage || data?.message || `HTTP ${response.status}`,
          data: null,
        };
      }
      return this.handleApiResponse(data, 'Create Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create course',
        data: null,
      };
    }
  }

  /**
   * PUT /Education/UpdateCourse (multipart/form-data)
   * fields include courseId; optional Thumbnail replaces existing image
   */
  async updateEducationCourse(fields, thumbnailFile = null) {
    try {
      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        if (value == null) return;
        formData.append(key, String(value));
      });
      if (thumbnailFile) {
        formData.append('Thumbnail', thumbnailFile, thumbnailFile.name || 'thumbnail.jpg');
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(apiConfig.endpoints.updateEducationCourse), {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.status === 401) {
        this.handleTokenExpiration();
        return { success: false, error: 'Authentication failed. Please login again.', data: null };
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.result || data?.responseMessage || data?.message || `HTTP ${response.status}`,
          data: null,
        };
      }
      return this.handleApiResponse(data, 'Update Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update course',
        data: null,
      };
    }
  }

  /**
   * POST /Education/ActivateCourse?courseId=
   */
  async activateEducationCourse(courseId) {
    try {
      const id = Number(courseId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid course id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.activateEducationCourse}?courseId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Activate Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to activate course',
        data: null,
      };
    }
  }

  /**
   * POST /Education/InactivateCourse?courseId=
   */
  async inactivateEducationCourse(courseId) {
    try {
      const id = Number(courseId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid course id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.inactivateEducationCourse}?courseId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Inactivate Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to inactivate course',
        data: null,
      };
    }
  }

  /**
   * DELETE /Education/DeleteCourse?courseId=
   */
  async deleteEducationCourse(courseId) {
    try {
      const id = Number(courseId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid course id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.deleteEducationCourse}?courseId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Education Course');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete course',
        data: null,
      };
    }
  }

  async getEducationModulesList(courseId) {
    try {
      const id = Number(courseId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid course id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.getEducationModulesList, {
        method: 'POST',
        body: JSON.stringify({ courseId: id }),
      });
      return this.handleApiResponse(response, 'Get Education Modules');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load modules',
        data: null,
      };
    }
  }

  async createEducationModule(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.createEducationModule, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Create Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create module',
        data: null,
      };
    }
  }

  async updateEducationModule(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.updateEducationModule, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Update Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update module',
        data: null,
      };
    }
  }

  async activateEducationModule(moduleId) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.activateEducationModule}?moduleId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Activate Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to activate module',
        data: null,
      };
    }
  }

  async inactivateEducationModule(moduleId) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.inactivateEducationModule}?moduleId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Inactivate Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to inactivate module',
        data: null,
      };
    }
  }

  async deleteEducationModule(moduleId) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.deleteEducationModule}?moduleId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete module',
        data: null,
      };
    }
  }

  async moveEducationModule(moduleId, direction) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.moveEducationModule, {
        method: 'POST',
        body: JSON.stringify({ courseModuleId: id, direction }),
      });
      return this.handleApiResponse(response, 'Move Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to move module',
        data: null,
      };
    }
  }

  async getEducationModuleById(moduleId) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.getEducationModuleById}?moduleId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Education Module');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load module',
        data: null,
      };
    }
  }

  async getEducationLessonsList(moduleId) {
    try {
      const id = Number(moduleId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid module id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.getEducationLessonsList, {
        method: 'POST',
        body: JSON.stringify({ moduleId: id }),
      });
      return this.handleApiResponse(response, 'Get Education Lessons');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load lessons',
        data: null,
      };
    }
  }

  async createEducationLesson(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.createEducationLesson, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Create Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create lesson',
        data: null,
      };
    }
  }

  async updateEducationLesson(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.updateEducationLesson, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Update Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update lesson',
        data: null,
      };
    }
  }

  async activateEducationLesson(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.activateEducationLesson}?lessonId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Activate Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to activate lesson',
        data: null,
      };
    }
  }

  async inactivateEducationLesson(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.inactivateEducationLesson}?lessonId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Inactivate Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to inactivate lesson',
        data: null,
      };
    }
  }

  async deleteEducationLesson(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.deleteEducationLesson}?lessonId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete lesson',
        data: null,
      };
    }
  }

  async moveEducationLesson(lessonId, direction) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.moveEducationLesson, {
        method: 'POST',
        body: JSON.stringify({ courseLessonId: id, direction }),
      });
      return this.handleApiResponse(response, 'Move Education Lesson');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to move lesson',
        data: null,
      };
    }
  }

  async getEducationLessonContent(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.getEducationLessonContent}?lessonId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Education Lesson Content');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load lesson content',
        data: null,
      };
    }
  }

  async saveEducationLessonArticle(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.saveEducationLessonArticle, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Save Education Lesson Article');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to save article',
        data: null,
      };
    }
  }

  async uploadEducationLessonVideo(lessonId, file) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      if (!file) {
        return { success: false, error: 'Video file is required', data: null };
      }

      const formData = new FormData();
      formData.append('lessonId', String(id));
      formData.append('file', file, file.name || 'video.mp4');

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(apiConfig.endpoints.uploadEducationLessonVideo), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.status === 401) {
        this.handleTokenExpiration();
        return { success: false, error: 'Authentication failed. Please login again.', data: null };
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.result || data?.responseMessage || data?.message || `HTTP ${response.status}`,
          data: null,
        };
      }
      return this.handleApiResponse(data, 'Upload Education Lesson Video');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload video',
        data: null,
      };
    }
  }

  async uploadEducationLessonPdf(lessonId, file) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      if (!file) {
        return { success: false, error: 'PDF file is required', data: null };
      }

      const formData = new FormData();
      formData.append('lessonId', String(id));
      formData.append('file', file, file.name || 'document.pdf');

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(apiConfig.endpoints.uploadEducationLessonPdf), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.status === 401) {
        this.handleTokenExpiration();
        return { success: false, error: 'Authentication failed. Please login again.', data: null };
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.result || data?.responseMessage || data?.message || `HTTP ${response.status}`,
          data: null,
        };
      }
      return this.handleApiResponse(data, 'Upload Education Lesson PDF');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload PDF',
        data: null,
      };
    }
  }

  async deleteEducationLessonContent(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.deleteEducationLessonContent}?lessonId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Education Lesson Content');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to remove content',
        data: null,
      };
    }
  }

  async getEducationQuizSettings(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.getEducationQuizSettings}?lessonId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Education Quiz Settings');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load quiz settings',
        data: null,
      };
    }
  }

  async saveEducationQuizSettings(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.saveEducationQuizSettings, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Save Education Quiz Settings');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to save quiz settings',
        data: null,
      };
    }
  }

  async getEducationQuizQuestionsList(lessonId) {
    try {
      const id = Number(lessonId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid lesson id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.getEducationQuizQuestionsList, {
        method: 'POST',
        body: JSON.stringify({ lessonId: id }),
      });
      return this.handleApiResponse(response, 'Get Education Quiz Questions');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load questions',
        data: null,
      };
    }
  }

  async getEducationQuizQuestionById(quizQuestionId) {
    try {
      const id = Number(quizQuestionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid question id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.getEducationQuizQuestionById}?quizQuestionId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load question',
        data: null,
      };
    }
  }

  async createEducationQuizQuestion(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.createEducationQuizQuestion, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Create Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create question',
        data: null,
      };
    }
  }

  async updateEducationQuizQuestion(payload) {
    try {
      const response = await this.request(apiConfig.endpoints.updateEducationQuizQuestion, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Update Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update question',
        data: null,
      };
    }
  }

  async activateEducationQuizQuestion(quizQuestionId) {
    try {
      const id = Number(quizQuestionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid question id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.activateEducationQuizQuestion}?quizQuestionId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Activate Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to activate question',
        data: null,
      };
    }
  }

  async inactivateEducationQuizQuestion(quizQuestionId) {
    try {
      const id = Number(quizQuestionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid question id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.inactivateEducationQuizQuestion}?quizQuestionId=${id}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Inactivate Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to inactivate question',
        data: null,
      };
    }
  }

  async deleteEducationQuizQuestion(quizQuestionId) {
    try {
      const id = Number(quizQuestionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid question id', data: null };
      }
      const response = await this.request(
        `${apiConfig.endpoints.deleteEducationQuizQuestion}?quizQuestionId=${id}`,
        { method: 'DELETE' }
      );
      return this.handleApiResponse(response, 'Delete Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete question',
        data: null,
      };
    }
  }

  async moveEducationQuizQuestion(quizQuestionId, direction) {
    try {
      const id = Number(quizQuestionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid question id', data: null };
      }
      const response = await this.request(apiConfig.endpoints.moveEducationQuizQuestion, {
        method: 'POST',
        body: JSON.stringify({ quizQuestionId: id, direction }),
      });
      return this.handleApiResponse(response, 'Move Education Quiz Question');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to move question',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetAnalyticsSummary
   */
  async getEducationAnalyticsSummary({ period = 'AllTime', fromDate = null, toDate = null } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationAnalyticsSummary, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Analytics Summary');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load analytics summary',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetCourseAnalytics
   */
  async getEducationCourseAnalytics({
    period = 'AllTime',
    fromDate = null,
    toDate = null,
    pageNumber = 1,
    pageSize = 10,
    searchTerm = '',
  } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationCourseAnalytics, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
          pageNumber,
          pageSize,
          searchTerm: searchTerm || null,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Course Analytics');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load course analytics',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetLearnerAnalytics
   */
  async getEducationLearnerAnalytics({
    period = 'AllTime',
    fromDate = null,
    toDate = null,
    pageNumber = 1,
    pageSize = 10,
    searchTerm = '',
    courseId = null,
    status = '',
    dateField = 'LastActivity',
  } = {}) {
    try {
      const cid = courseId != null && courseId !== '' ? Number(courseId) : null;
      const response = await this.request(apiConfig.endpoints.getEducationLearnerAnalytics, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
          pageNumber,
          pageSize,
          searchTerm: searchTerm || null,
          courseId: Number.isFinite(cid) && cid > 0 ? cid : null,
          status: status || null,
          dateField: dateField || 'LastActivity',
        }),
      });
      return this.handleApiResponse(response, 'Get Education Learner Analytics');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load learner analytics',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetQuizAnalytics
   */
  async getEducationQuizAnalytics({
    period = 'AllTime',
    fromDate = null,
    toDate = null,
    pageNumber = 1,
    pageSize = 10,
    courseId = null,
    lessonId = null,
    searchTerm = '',
  } = {}) {
    try {
      const cid = courseId != null && courseId !== '' ? Number(courseId) : null;
      const lid = lessonId != null && lessonId !== '' ? Number(lessonId) : null;
      const response = await this.request(apiConfig.endpoints.getEducationQuizAnalytics, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
          pageNumber,
          pageSize,
          courseId: Number.isFinite(cid) && cid > 0 ? cid : null,
          lessonId: Number.isFinite(lid) && lid > 0 ? lid : null,
          searchTerm: searchTerm || null,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Quiz Analytics');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load quiz analytics',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetLearningActivity
   */
  async getEducationLearningActivity({
    period = 'AllTime',
    fromDate = null,
    toDate = null,
    pageNumber = 1,
    pageSize = 10,
  } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationLearningActivity, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
          pageNumber,
          pageSize,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Learning Activity');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load learning activity',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetPopularCourses
   */
  async getEducationPopularCourses({ period = 'AllTime', fromDate = null, toDate = null } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationPopularCourses, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Popular Courses');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load popular courses',
        data: null,
      };
    }
  }

  /**
   * POST /Education/GetCompletionAnalytics
   */
  async getEducationCompletionAnalytics({ period = 'AllTime', fromDate = null, toDate = null } = {}) {
    try {
      const response = await this.request(apiConfig.endpoints.getEducationCompletionAnalytics, {
        method: 'POST',
        body: JSON.stringify({
          period: period || 'AllTime',
          fromDate: fromDate || null,
          toDate: toDate || null,
        }),
      });
      return this.handleApiResponse(response, 'Get Education Completion Analytics');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load completion analytics',
        data: null,
      };
    }
  }

  /**
   * POST /Affiliate/SendSubPartnerInvite
   * Body: { email, percentage }
   * Success: responseCode === 0
   */
  async sendSubPartnerInvite(email, percentage) {
    try {
      const trimmed = String(email || '').trim();
      if (!trimmed) {
        return { success: false, error: 'Email is required', data: null };
      }

      const pct = Number(percentage);
      if (!Number.isFinite(pct)) {
        return { success: false, error: 'Commission percentage is required', data: null };
      }

      const response = await this.request(apiConfig.endpoints.sendSubPartnerInvite, {
        method: 'POST',
        body: JSON.stringify({ email: trimmed, percentage: pct }),
      });

      return this.handleApiResponse(response, 'Send Sub Partner Invite');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send invite',
        data: null,
      };
    }
  }

  // Get Instrument Groups
  async getInstrumentGroups() {
    const response = await this.request(apiConfig.endpoints.getInstrumentGroups, {
      method: 'GET',
    });
    return this.handleApiResponse(response, 'Get Instrument Groups');
  }

  /** Catalog of affiliate promotions. Success: responseCode === 0 */
  async getAffiliatePromotions(triggerEvent = null) {
    try {
      const query = new URLSearchParams();
      if (triggerEvent) query.set('triggerEvent', String(triggerEvent));
      const qs = query.toString();
      const url = qs
        ? `${apiConfig.endpoints.getAffiliatePromotions}?${qs}`
        : apiConfig.endpoints.getAffiliatePromotions;
      const response = await this.request(url, { method: 'GET' });
      return this.handleApiResponse(response, 'Get Affiliate Promotions');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load promotions',
        data: [],
      };
    }
  }

  /** Logged-in affiliate promotion history. Success: responseCode === 0 */
  async getMyAffiliatePromotions() {
    try {
      const response = await this.request(apiConfig.endpoints.getMyAffiliatePromotions, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get My Affiliate Promotions');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load my promotions',
        data: [],
      };
    }
  }

  /**
   * POST /AffiliatePromotions/ClaimAffiliatePromotion
   * Body: { affiliatePromotionId }
   * Creates Pending history + notification. Success: responseCode === 0
   */
  async claimAffiliatePromotion(affiliatePromotionId) {
    try {
      const id = Number(affiliatePromotionId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid promotion id', data: null };
      }

      const response = await this.request(apiConfig.endpoints.claimAffiliatePromotion, {
        method: 'POST',
        body: JSON.stringify({ affiliatePromotionId: id }),
      });

      return this.handleApiResponse(response, 'Claim Affiliate Promotion');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to claim promotion',
        data: null,
      };
    }
  }

  /**
   * GET /Challenges/GetChallengeOverview
   * Weekly Challenge banner/countdown/tiles for the logged-in affiliate. Success: responseCode === 0
   */
  async getChallengeOverview() {
    try {
      const response = await this.request(apiConfig.endpoints.getChallengeOverview, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Challenge Overview');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load challenge overview',
        data: null,
      };
    }
  }

  /**
   * POST /Challenges/GetChallengeParticipants
   * Paginated per-referred-user progress list for the Challenges List table.
   */
  async getChallengeParticipants(params = {}) {
    const payload = {
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      searchTerm: params.searchTerm ?? '',
      dateFrom: params.dateFrom || null,
      dateTo: params.dateTo || null,
      type: '',
      sortBy: '',
      sortOrder: '',
    };
    try {
      const response = await this.request(apiConfig.endpoints.getChallengeParticipants, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return this.handleApiResponse(response, 'Get Challenge Participants');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load challenge participants',
        data: null,
      };
    }
  }

  /**
   * GET /AffiliateNotifications/GetNotifications?take=50
   * History for bell / notifications page. Success: responseCode === 0
   */
  async getAffiliateNotifications(take = 50) {
    try {
      const limit = Math.min(200, Math.max(1, Number(take) || 50));
      const response = await this.request(
        `${apiConfig.endpoints.getAffiliateNotifications}?take=${limit}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Affiliate Notifications');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load notifications',
        data: [],
      };
    }
  }

  /**
   * POST /AffiliateNotifications/MarkAsRead
   * Body: { notificationIds?: number[] } — empty/omit = mark all unread
   */
  async markAffiliateNotificationsRead(notificationIds = null) {
    try {
      const ids = Array.isArray(notificationIds)
        ? notificationIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
        : [];

      const response = await this.request(apiConfig.endpoints.markAffiliateNotificationsRead, {
        method: 'POST',
        body: JSON.stringify(
          ids.length > 0 ? { notificationIds: ids } : { notificationIds: null }
        ),
      });

      return this.handleApiResponse(response, 'Mark Affiliate Notifications Read');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to mark notifications as read',
        data: null,
      };
    }
  }

  /**
   * GET /AffiliateNotifications/GetPreferences → { receiveNotifications }
   */
  async getAffiliateNotificationPreferences() {
    try {
      const response = await this.request(apiConfig.endpoints.getAffiliateNotificationPreferences, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get Notification Preferences');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load notification preferences',
        data: null,
      };
    }
  }

  /**
   * POST /AffiliateNotifications/UpdatePreferences
   * Body: { receiveNotifications: boolean } — saved to AffiliateUsers.ReceiveNotifications
   */
  async updateAffiliateNotificationPreferences(receiveNotifications) {
    try {
      const response = await this.request(apiConfig.endpoints.updateAffiliateNotificationPreferences, {
        method: 'POST',
        body: JSON.stringify({ receiveNotifications: Boolean(receiveNotifications) }),
      });
      return this.handleApiResponse(response, 'Save Notification Preferences');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to save notification preferences',
        data: null,
      };
    }
  }

  // Get Instrument Commissions by affiliate level
  async getInstrumentCommissions(affiliateLevelId) {
    const query = new URLSearchParams({
      affiliateLevelId: String(affiliateLevelId ?? 0),
    });
    const response = await this.request(
      `${apiConfig.endpoints.getInstrumentCommissions}?${query.toString()}`,
      { method: 'GET' }
    );
    return this.handleApiResponse(response, 'Get Instrument Commissions');
  }

  /** Account Level points calculator map. Success: responseCode === 1 */
  async getPointsCalculatorList() {
    try {
      const response = await this.request(apiConfig.endpoints.getPointsCalculatorList, {
        method: 'GET',
      });
      const code = response?.response?.responseCode ?? response?.responseCode;
      const message =
        response?.response?.responseMessage ??
        response?.responseMessage ??
        'Points calculator list retrieved successfully';
      const result = response?.result ?? response?.data ?? null;

      if (code === 1) {
        return { success: true, message, data: Array.isArray(result) ? result : [] };
      }

      if (code != null) {
        return {
          success: false,
          error: message || 'Failed to load points calculator list',
          data: [],
        };
      }

      return this.handleApiResponse(response, 'Get Points Calculator List');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load points calculator list',
        data: [],
      };
    }
  }

  // Update Password API
  async updatePassword(currentPassword, newPassword, confirmPassword) {
    const payload = {
      currentPassword,
      newPassword,
      confirmPassword
    };

    const response = await this.request('/Affiliate/UpdatePassword', {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Update Password');
  }

  // Create Fund Transfer API
  async createFundTransfer(data) {
    const response = await this.request(apiConfig.endpoints.createFundTransfer, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });

    // Return raw response for manual handling in component
    return response;
  }

  // Get Internal Transfer List API
  async getInternalTransferList(params = {}) {
    const payload = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      searchTerm: params.searchTerm || "",
      type: params.type || "S"
    };

    const inflightKey = [
      apiConfig.endpoints.getInternalTransferList,
      payload.pageNumber,
      payload.pageSize,
      payload.searchTerm,
      payload.type,
    ].join('|');

    if (postRequestInflight.has(inflightKey)) {
      return postRequestInflight.get(inflightKey);
    }

    const requestPromise = this.request(apiConfig.endpoints.getInternalTransferList, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    })
      .then((response) => this.handleApiResponse(response, 'Get Internal Transfer List'))
      .finally(() => {
        postRequestInflight.delete(inflightKey);
      });

    postRequestInflight.set(inflightKey, requestPromise);
    return requestPromise;
  }

  // Get Reward List API
  async getRewardList() {
    try {
      const response = await this.request('/Rewards/GetRewardList', {
        method: 'GET'
      });
      
      // Handle response format where responseCode 1 means success
      if (response.response && response.response.responseCode === 1) {
        return {
          success: true,
          message: response.response.responseMessage || 'Rewards retrieved successfully',
          data: response.result || []
        };
      }
      
      // Handle case where responseCode is 0 (also success in some APIs)
      if (response.response && response.response.responseCode === 0) {
        return {
          success: true,
          message: response.response.responseMessage || 'Rewards retrieved successfully',
          data: response.result || []
        };
      }
      
      // Error case
      return {
        success: false,
        error: 'Unexpected response format',
        data: []
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch rewards',
        data: []
      };
    }
  }

  parseRewardsSuccessResponse(response, fallbackMessage) {
    if (response?.response && (response.response.responseCode === 1 || response.response.responseCode === 0)) {
      return {
        success: true,
        message: response.response.responseMessage || fallbackMessage,
        data: response.result || [],
      };
    }
    return {
      success: false,
      error: response?.response?.responseMessage || 'Unexpected response format',
      data: [],
    };
  }

  async getRewardTierList() {
    try {
      const response = await this.request(apiConfig.endpoints.getRewardTierList, { method: 'GET' });
      return this.parseRewardsSuccessResponse(response, 'Reward tier list retrieved successfully');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch reward tiers',
        data: [],
      };
    }
  }

  async getRewardTierPointsList() {
    try {
      const response = await this.request(apiConfig.endpoints.getRewardTierPointsList, {
        method: 'GET',
      });
      return this.parseRewardsSuccessResponse(
        response,
        'Reward tier points list retrieved successfully'
      );
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch reward tier points',
        data: [],
      };
    }
  }

  // Get Claimed Rewards History API
  async getClaimRewardHistoryAll() {
    try {
      const response = await this.request(apiConfig.endpoints.getClaimRewardHistoryAll, {
        method: 'GET'
      });
      
      // Handle response format where responseCode 1 means success
      if (response.response && response.response.responseCode === 1) {
        return {
          success: true,
          message: response.response.responseMessage || 'Claimed rewards retrieved successfully',
          data: response.result || []
        };
      }
      
      // Handle case where responseCode is 0 (also success in some APIs)
      if (response.response && response.response.responseCode === 0) {
        return {
          success: true,
          message: response.response.responseMessage || 'Claimed rewards retrieved successfully',
          data: response.result || []
        };
      }
      
      // Error case
      return {
        success: false,
        error: 'Unexpected response format',
        data: []
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch claimed rewards',
        data: []
      };
    }
  }

  async buildRewardsBundle() {
    const [listRes, claimedRes, widgetsRes, commissionRes, tierRes, tierPointsRes, loyaltyRes] =
      await Promise.all([
        this.getRewardList().catch(() => ({ success: false, data: [] })),
        this.getClaimRewardHistoryAll().catch(() => ({ success: false, data: [] })),
        this.getDashboardWidgets().catch(() => ({ success: false, data: null })),
        this.getCommissionWidgets().catch(() => ({ success: false, data: null })),
        this.getRewardTierList().catch(() => ({ success: false, data: [] })),
        this.getRewardTierPointsList().catch(() => ({ success: false, data: [] })),
        this.getLoyaltySummary().catch(() => ({ success: false, data: null })),
      ]);

    const rewards = normalizeRewardList(listRes);
    const claimed = enrichClaimHistoryWithRewards(
      normalizeClaimHistory(claimedRes),
      rewards
    );
    const progress = extractRewardProgress(widgetsRes, commissionRes);

    // AffiliateWallet points prefer GetRewardsDashboard, else GetLoyaltySummary.
    let availableLoyaltyPoints = Number.NaN;
    let totalLoyaltyPoints = Number.NaN;
    try {
      const dashRes = await this.getRewardsDashboard();
      if (dashRes?.success && dashRes.data) {
        availableLoyaltyPoints = Number(dashRes.data.availablePoints);
        totalLoyaltyPoints = Number(dashRes.data.totalPointsEarned);
      }
    } catch {
      /* fall through to loyalty summary */
    }

    const loyalty = loyaltyRes?.success ? loyaltyRes.data || {} : {};
    if (!Number.isFinite(availableLoyaltyPoints)) {
      availableLoyaltyPoints = Number(
        loyalty.availableLoyaltyPoints ?? loyalty.availablePoints ?? loyalty.loyaltyPoints ?? NaN
      );
    }
    if (!Number.isFinite(totalLoyaltyPoints)) {
      totalLoyaltyPoints = Number(
        loyalty.totalLoyaltyPoints ?? loyalty.totalPointsEarned ?? NaN
      );
    }

    if (Number.isFinite(availableLoyaltyPoints)) {
      progress.availableLoyaltyPoints = availableLoyaltyPoints;
      progress.availablePoints = availableLoyaltyPoints;
    }
    if (Number.isFinite(totalLoyaltyPoints)) {
      progress.totalLoyaltyPoints = totalLoyaltyPoints;
      progress.totalPointsEarned = totalLoyaltyPoints;
    }
    // Prefer spendable balance for claim affordability checks.
    progress.rewardPoints = Number.isFinite(availableLoyaltyPoints)
      ? availableLoyaltyPoints
      : totalLoyaltyPoints;

    const tiersFromList = normalizeRewardTierList(tierRes);
    const rewardTiers =
      tiersFromList.length > 0 ? tiersFromList : normalizeRewardTierList(tierPointsRes);
    const summary = buildRewardsSummary({ rewards, claimed, progress, rewardTiers });
    const milestones = buildMilestones(rewards, progress, claimed);
    const catalog = enrichRewardsForClaim(rewards, progress, claimed);

    return {
      success: true,
      rewards,
      claimed,
      progress,
      summary,
      milestones,
      catalog,
      rewardTiers,
      loyalty,
      listRes,
      claimedRes,
      tierRes,
      loyaltyRes,
    };
  }

  async getRewardsPointsSummary() {
    try {
      const dash = await this.getRewardsDashboard();
      if (!dash.success) return dash;
      return {
        success: true,
        data: dash.data,
        message: dash.message || 'Rewards points summary retrieved successfully',
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch points summary', data: null };
    }
  }

  async getMilestoneProgress() {
    try {
      const response = await this.request(apiConfig.endpoints.getMilestoneProgress, {
        method: 'GET',
      });
      const code = Number(
        response?.response?.responseCode ?? response?.responseCode ?? response?.Response?.ResponseCode
      );
      const payload =
        response?.result ??
        response?.Result ??
        response?.data ??
        response?.Data ??
        null;

      if (code === 1 || code === 0) {
        return {
          success: true,
          message:
            response?.response?.responseMessage ||
            response?.responseMessage ||
            'Milestone progress retrieved successfully',
          data: parseMilestoneProgress(payload ?? response),
        };
      }

      if (response?.success && (response?.data || response?.result)) {
        return {
          success: true,
          message: response.message || 'Milestone progress retrieved successfully',
          data: parseMilestoneProgress(response.data ?? response.result),
        };
      }

      return this.handleApiResponse(response, 'Milestone Progress');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch milestone progress',
        data: null,
      };
    }
  }

  async getRewardsDashboard() {
    try {
      const response = await this.request(apiConfig.endpoints.getRewardsDashboard, { method: 'GET' });
      const code = Number(
        response?.response?.responseCode ?? response?.responseCode ?? response?.Response?.ResponseCode
      );
      const payload =
        response?.result ??
        response?.Result ??
        response?.data ??
        response?.Data ??
        null;

      if (code === 1 || code === 0) {
        return {
          success: true,
          message:
            response?.response?.responseMessage ||
            response?.responseMessage ||
            'Rewards dashboard retrieved successfully',
          data: parseRewardsDashboard(payload ?? response),
        };
      }

      // Some gateways wrap again: { success, data: { response, result } }
      if (response?.success && (response?.data || response?.result)) {
        return {
          success: true,
          message: response.message || 'Rewards dashboard retrieved successfully',
          data: parseRewardsDashboard(response.data ?? response.result),
        };
      }

      return this.handleApiResponse(response, 'Rewards Dashboard');
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch rewards dashboard', data: null };
    }
  }

  async getRewardsReport(status = '') {
    try {
      const bundle = await this.buildRewardsBundle();
      const normalized = bundle.claimed;
      const statusKey = String(status || '').toLowerCase();
      const filtered = !statusKey
        ? normalized
        : normalized.filter((item) => {
            if (statusKey === 'pending') return !item.isClaimed;
            if (statusKey === 'redeemed') return item.isClaimed;
            return true;
          });

      return {
        success: true,
        message: 'Rewards report retrieved successfully',
        data: {
          history: filtered,
          historyCount: filtered.length,
          pendingCount: normalized.filter((i) => !i.isClaimed).length,
          claimedCount: normalized.filter((i) => i.isClaimed).length,
          pointsToSpend: bundle.summary.availablePoints ?? bundle.summary.pointsToSpend,
          availablePoints: bundle.summary.availablePoints ?? bundle.summary.pointsToSpend,
          totalPointsEarned: bundle.summary.totalPointsEarned,
          rewardTierName: bundle.summary.rewardTierName,
          rewardCurrentLevel: bundle.progress.rewardCurrentLevel,
        },
      };
    } catch (error) {
      return {
        success: true,
        data: { history: [], historyCount: 0, pendingCount: 0, claimedCount: 0, pointsToSpend: 0 },
        message: error.message || 'No rewards report available',
      };
    }
  }

  /**
   * GET /Rewards/GetRewardCalculationList
   * Cash / Credit receive percentages. Success: responseCode === 1
   */
  async getRewardCalculationList() {
    try {
      const response = await this.request(apiConfig.endpoints.getRewardCalculationList, {
        method: 'GET',
      });
      const code = response?.response?.responseCode ?? response?.responseCode;
      const message =
        response?.response?.responseMessage ??
        response?.responseMessage ??
        'Reward calculation list retrieved successfully';
      const result = response?.result ?? response?.data ?? null;

      if (code === 1) {
        const list = Array.isArray(result) ? result : [];
        return {
          success: true,
          message,
          data: list.map((row) => {
            const type = String(
              row.affiliateRewardCalculationType ??
                row.AffiliateRewardCalculationType ??
                row.rewardCalculationType ??
                row.RewardCalculationType ??
                ''
            ).trim();
            const percentage = Number(
              row.affiliateRewardCalculationPercentage ??
                row.AffiliateRewardCalculationPercentage ??
                row.rewardCalculationPercentage ??
                row.RewardCalculationPercentage ??
                0
            );
            return {
              affiliateRewardCalculationId:
                row.affiliateRewardCalculationId ??
                row.AffiliateRewardCalculationId ??
                row.rewardCalculationId ??
                row.RewardCalculationId ??
                null,
              affiliateRewardCalculationType: type,
              affiliateRewardCalculationPercentage: Number.isFinite(percentage)
                ? percentage
                : 0,
              // aliases used by claim modal
              rewardCalculationType: type,
              rewardCalculationPercentage: Number.isFinite(percentage) ? percentage : 0,
              raw: row,
            };
          }),
        };
      }

      if (code != null) {
        return {
          success: false,
          error: message || 'Reward calculation unavailable',
          data: [],
        };
      }

      return this.handleApiResponse(response, 'Get Reward Calculation List');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Reward calculation unavailable',
        data: [],
      };
    }
  }

  /** @deprecated Use getRewardCalculationList */
  async getRewardCalculations() {
    return this.getRewardCalculationList();
  }

  /**
   * POST /Rewards/ClaimReward
   * Body: { rewardId, claimType: "cash"|"credit" }
   * Success: responseCode === 1
   * ClaimReceivedAmount = RewardValue × percentage / 100 (server-side).
   * Points deducted = RewardRequiredPoints (cost only).
   */
  async claimReward(rewardId, claimType = 'cash') {
    try {
      const id = Number(rewardId);
      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Invalid reward id', data: null };
      }

      const typeKey =
        String(claimType).toLowerCase() === 'credit' || Number(claimType) === 1
          ? 'credit'
          : 'cash';

      const payload = {
        rewardId: id,
        affiliateRewardId: id,
        claimType: typeKey,
      };

      const response = await this.request(apiConfig.endpoints.claimReward, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const code = response?.response?.responseCode ?? response?.responseCode;
      const message =
        response?.response?.responseMessage ??
        response?.responseMessage ??
        '';
      const result = response?.result ?? null;

      if (code === 1) {
        return {
          success: true,
          message: message || 'Reward claimed successfully',
          data: result,
        };
      }

      if (code === 0 || code != null) {
        const required = result?.requiredPoints ?? result?.RequiredPoints;
        const available = result?.availablePoints ?? result?.AvailablePoints;
        let error = message || 'Failed to claim reward';
        if (
          required != null &&
          available != null &&
          !/insufficient/i.test(error)
        ) {
          error = `Insufficient available points. Required: ${required}, Available: ${available}`;
        }
        return { success: false, error, data: result };
      }

      return this.handleApiResponse(response, 'Claim Reward');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to claim reward',
        data: null,
      };
    }
  }

  /** Loyalty APIs use responseCode 1 for success (same pattern as Rewards). */
  parseLoyaltySuccessResponse(response, fallbackMessage) {
    const code = response?.response?.responseCode ?? response?.responseCode;
    const message =
      response?.response?.responseMessage ??
      response?.responseMessage ??
      fallbackMessage;
    const result = response?.result ?? response?.data ?? null;

    if (code === 1) {
      return { success: true, message: message || fallbackMessage, data: result };
    }

    if (code != null) {
      return {
        success: false,
        error: message || fallbackMessage,
        data: result,
      };
    }

    return this.handleApiResponse(response, fallbackMessage);
  }

  async getLoyaltySummary() {
    try {
      const response = await this.request(apiConfig.endpoints.getLoyaltySummary, { method: 'GET' });
      const parsed = this.parseLoyaltySuccessResponse(response, 'Loyalty summary retrieved successfully');
      if (!parsed.success) return parsed;
      return {
        success: true,
        message: parsed.message,
        data: parseLoyaltySummary(parsed.data),
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load loyalty summary',
        data: null,
      };
    }
  }

  async getLoyaltyPointsSummary() {
    return this.getLoyaltySummary();
  }

  async getLoyaltyDashboard() {
    return this.getLoyaltySummary();
  }

  async getLoyaltyEventsList() {
    try {
      const response = await this.request(apiConfig.endpoints.getLoyaltyEventsList, { method: 'GET' });
      const parsed = this.parseLoyaltySuccessResponse(response, 'Loyalty events retrieved successfully');
      if (!parsed.success) return parsed;
      return {
        success: true,
        message: parsed.message,
        data: normalizeLoyaltyEvents(parsed.data),
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load loyalty events',
        data: [],
      };
    }
  }

  async getLoyaltyEventPointsList() {
    try {
      const response = await this.request(apiConfig.endpoints.getLoyaltyEventPointsList, {
        method: 'GET',
      });
      const parsed = this.parseLoyaltySuccessResponse(
        response,
        'Loyalty event points retrieved successfully'
      );
      if (!parsed.success) return parsed;
      return {
        success: true,
        message: parsed.message,
        data: parsed.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load loyalty event points',
        data: [],
      };
    }
  }

  async getLoyaltyTiersList() {
    try {
      const response = await this.request(apiConfig.endpoints.getLoyaltyTiersList, { method: 'GET' });
      const parsed = this.parseLoyaltySuccessResponse(response, 'Loyalty tiers retrieved successfully');
      if (!parsed.success) return parsed;
      return {
        success: true,
        message: parsed.message,
        data: normalizeLoyaltyTiers(parsed.data),
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load loyalty tiers',
        data: [],
      };
    }
  }

  async getLoyaltyPointsHistory(params = {}) {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? 10;

    try {
      const query = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
      }).toString();
      const response = await this.request(
        `${apiConfig.endpoints.getLoyaltyPointsHistory}?${query}`,
        { method: 'GET' }
      );
      const parsed = this.parseLoyaltySuccessResponse(
        response,
        'Loyalty points history retrieved successfully'
      );
      if (!parsed.success) return parsed;
      const normalized = normalizeLoyaltyHistory(parsed.data);
      return {
        success: true,
        message: parsed.message,
        data: {
          history: normalized.rows,
          pagination: normalized.pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load loyalty points history',
        data: { history: [], pagination: { pageNumber, pageSize, totalCount: 0, totalPages: 0 } },
      };
    }
  }

  async getLoyaltyHistory(params = {}) {
    return this.getLoyaltyPointsHistory(params);
  }

  async getLoyaltyEarnRules() {
    return this.getLoyaltyEventsList();
  }

  async getKycDocumentCategories() {
    try {
      const response = await this.request(apiConfig.endpoints.getKycDocumentCategories, {
        method: 'GET',
      });
      return this.handleApiResponse(response, 'Get KYC Document Categories');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load document categories',
        data: null,
      };
    }
  }

  async getKycDocumentTypes(documentCategoryId) {
    try {
      const id = Number(documentCategoryId);
      const response = await this.request(
        `${apiConfig.endpoints.getKycDocumentTypes}?documentCategoryId=${id}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get KYC Document Types');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load document types',
        data: null,
      };
    }
  }

  async getKycMyDocuments() {
    try {
      const response = await this.request(apiConfig.endpoints.getKycMyDocuments, { method: 'GET' });
      return this.handleApiResponse(response, 'Get My KYC Documents');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to load documents',
        data: null,
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Partner Agreements — the API resolves the Partner from the auth token; no Partner id is ever sent.
  // ---------------------------------------------------------------------------

  async getMyAgreements() {
    try {
      const response = await this.request(apiConfig.endpoints.getMyAgreements, { method: 'GET' });
      return this.handleApiResponse(response, 'Get Agreements');
    } catch (error) {
      return { success: false, error: friendlyAgreementError(error, 'Failed to load agreements'), data: null };
    }
  }

  async getMyAgreementById(mappingId) {
    try {
      const response = await this.request(
        `${apiConfig.endpoints.getMyAgreementById}/${encodeURIComponent(mappingId)}`,
        { method: 'GET' }
      );
      return this.handleApiResponse(response, 'Get Agreement');
    } catch (error) {
      return { success: false, error: friendlyAgreementError(error, 'Failed to load agreement'), data: null };
    }
  }

  async startAgreementSignature(mappingId) {
    try {
      const response = await this.request(
        `${apiConfig.endpoints.startAgreementSignature}/${encodeURIComponent(mappingId)}`,
        { method: 'POST' }
      );
      return this.handleApiResponse(response, 'Start Signature');
    } catch (error) {
      return { success: false, error: friendlyAgreementError(error, 'Failed to start signature'), data: null };
    }
  }

  async completeAgreementSignature(mappingId, { affiliateAgreementSignatureId, signature, consent, documentHash }) {
    try {
      const response = await this.request(
        `${apiConfig.endpoints.completeAgreementSignature}/${encodeURIComponent(mappingId)}`,
        {
          method: 'POST',
          body: JSON.stringify({ affiliateAgreementSignatureId, signature, consent, documentHash }),
        }
      );
      return this.handleApiResponse(response, 'Sign Agreement');
    } catch (error) {
      return { success: false, error: friendlyAgreementError(error, 'Failed to sign agreement'), data: null };
    }
  }

  /**
   * Fetches an agreement PDF as a Blob (original, or the Partner's signed copy when `signed`).
   * Files are streamed only through the authorized API, never from a public URL.
   */
  async getAgreementPdf(mappingId, { signed = false, mode = 'view' } = {}) {
    const endpoint = signed ? apiConfig.endpoints.downloadSignedAgreement : apiConfig.endpoints.downloadAgreementFile;
    const url = getApiUrl(`${endpoint}/${encodeURIComponent(mappingId)}?mode=${mode}`);
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    headers.Accept = 'application/pdf, application/json';

    let response;
    try {
      response = await fetch(url, { method: 'GET', headers });
    } catch (error) {
      throw new Error(friendlyAgreementError(error, 'Unable to load the document'));
    }
    if (response.status === 401) {
      this.handleTokenExpiration();
      throw new Error('Authentication failed. Please login again.');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.response?.responseMessage ||
          errorData.message ||
          (signed ? 'The signed agreement is not available' : 'The agreement document is currently unavailable')
      );
    }
    return response.blob();
  }

  /**
   * GET /AffiliateDocuments/GetAffiliateKYCStatus (alias: GetKYCStatus)
   * Merges GetMyDocuments when status payload has no documents list.
   */
  async getKycStatus() {
    let statusData = null;

    const tryStatus = async (path) => {
      const response = await this.request(path, { method: 'GET' });
      const parsed = this.handleApiResponse(response, 'KYC Status');
      if (parsed.success && parsed.data) return parsed.data;
      return null;
    };

    try {
      statusData = await tryStatus(apiConfig.endpoints.getKycStatus);
    } catch (error) {
      // primary may 404 on older builds
    }

    if (!statusData && apiConfig.endpoints.getKycStatusAlias) {
      try {
        statusData = await tryStatus(apiConfig.endpoints.getKycStatusAlias);
      } catch (error) {
        // continue to documents merge
      }
    }

    try {
      const docsRes = await this.getKycMyDocuments();
      const docsPayload = docsRes.success ? docsRes.data : [];
      const base =
        statusData && typeof statusData === 'object' && !Array.isArray(statusData)
          ? statusData
          : {};

      const statusDocs = base.documents ?? base.Documents;
      const statusDocsList = Array.isArray(statusDocs)
        ? statusDocs
        : statusDocs && typeof statusDocs === 'object'
          ? statusDocs
          : null;
      const hasStatusDocs = Array.isArray(statusDocsList)
        ? statusDocsList.length > 0
        : Boolean(statusDocsList);

      return {
        success: true,
        message: statusData ? 'KYC Status' : 'KYC status derived from documents',
        data: {
          ...base,
          documents: hasStatusDocs ? statusDocsList : docsPayload,
          myDocuments: docsPayload,
        },
      };
    } catch (error) {
      if (statusData) {
        return { success: true, message: 'KYC Status', data: statusData };
      }
      return { success: false, error: error.message || 'Failed to fetch KYC status', data: null };
    }
  }

  async getKycDetails() {
    return this.getKycMyDocuments();
  }

  /**
   * POST /AffiliateDocuments/UploadDocuments
   * multipart named fields: DocumentTypeId, Front, Back, Selfie, PriorVerificationFailures
   * Legacy files[] is used only when named sides are not provided.
   */
  async uploadKycDocuments(documentTypeId, files = [], options = {}) {
    try {
      const id = Number(documentTypeId);
      const frontFile = options.frontFile || options.Front || null;
      const backFile = options.backFile || options.Back || null;
      const selfieFile = options.selfieFile || options.Selfie || null;
      const prior =
        options.priorVerificationFailures ?? options.PriorVerificationFailures ?? 0;
      const useNamedSides = Boolean(frontFile || backFile || selfieFile);
      const list = (Array.isArray(files) ? files : [files]).filter(Boolean);

      if (!Number.isFinite(id) || id <= 0) {
        return { success: false, error: 'Document type is required', data: null };
      }
      if (!useNamedSides && !list.length) {
        return { success: false, error: 'At least one file is required', data: null };
      }

      const formData = new FormData();
      formData.append('DocumentTypeId', String(id));
      formData.append('PriorVerificationFailures', String(Math.max(0, Number(prior) || 0)));

      if (useNamedSides) {
        if (frontFile) formData.append('Front', frontFile, frontFile.name || 'kyc-front.jpg');
        if (backFile) formData.append('Back', backFile, backFile.name || 'kyc-back.jpg');
        if (selfieFile) {
          const mime = String(selfieFile.type || '').toLowerCase();
          if (!mime.startsWith('image/')) {
            return { success: false, error: 'Selfie must be a JPG or PNG image.', data: null };
          }
          formData.append('Selfie', selfieFile, selfieFile.name || 'kyc-selfie.jpg');
        }
      } else {
        list.forEach((file) => {
          formData.append('Files', file);
        });
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(apiConfig.endpoints.uploadKycDocuments), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          this.formatApiError(errorData, 'Upload KYC Documents')
            || `HTTP error! status: ${response.status}`
        );
      }

      const json = await response.json();
      const parsed = this.handleApiResponse(json, 'Upload KYC Documents');
      const result = parsed.data && typeof parsed.data === 'object' ? parsed.data : {};
      const apiMsg = String(
        json?.response?.responseMessage ?? parsed.message ?? parsed.error ?? ''
      ).trim();
      const verificationMessage = String(
        result.verificationMessage ?? result.VerificationMessage ?? apiMsg
      ).trim();
      const verificationOutcome = String(
        result.verificationOutcome ?? result.VerificationOutcome ?? ''
      ).toLowerCase();

      return {
        ...parsed,
        message: verificationMessage || parsed.message,
        autoVerified: Boolean(result.autoVerified ?? result.AutoVerified),
        verificationOutcome,
        failedAttemptNumber: result.failedAttemptNumber ?? result.FailedAttemptNumber ?? null,
        docsStatus: result.docsStatus ?? result.DocsStatus ?? '',
        verificationStatus: result.verificationStatus ?? result.VerificationStatus ?? '',
        verificationMessage,
        faceMatchScore: result.faceMatchScore ?? result.FaceMatchScore ?? null,
        pendingAdminApproval: Boolean(result.pendingAdminApproval ?? result.PendingAdminApproval),
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to upload documents',
        data: null,
      };
    }
  }

  async uploadKycIdentity(formData) {
    // Legacy wizard helper — prefer uploadKycDocuments
    try {
      const typeId = formData.get('documentTypeId') || formData.get('DocumentTypeId');
      const files = [];
      formData.forEach((value, key) => {
        if (value instanceof File && /file|front|back/i.test(key)) files.push(value);
      });
      if (!files.length) {
        const front = formData.get('front');
        const back = formData.get('back');
        if (front instanceof File) files.push(front);
        if (back instanceof File) files.push(back);
      }
      return this.uploadKycDocuments(typeId, files);
    } catch (error) {
      return { success: false, error: error.message || 'Failed to upload identity document', data: null };
    }
  }

  async uploadKycAddress(formData) {
    try {
      const typeId = formData.get('documentTypeId') || formData.get('DocumentTypeId');
      const file = formData.get('file') || formData.get('Files') || formData.get('files');
      return this.uploadKycDocuments(typeId, file instanceof File ? [file] : []);
    } catch (error) {
      return { success: false, error: error.message || 'Failed to upload address document', data: null };
    }
  }

  async saveKycPersonalDetails(payload) {
    return { success: true, data: payload, message: 'Profile details are managed on Profile page' };
  }

  async submitKyc() {
    return this.getKycStatus();
  }

  // Create Client Campaign API
  async createClientCampaign(campaignName) {
    const payload = {
      campaignName: campaignName
    };

    const response = await this.request(apiConfig.endpoints.createClientCampaign, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Create Client Campaign');
  }

  // Get Client Campaigns List API
  async getClientCampaignsList(params = {}) {
    const payload = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      searchTerm: params.searchTerm || "",
      type: params.type || "",
      sortBy: params.sortBy || "",
      sortOrder: params.sortOrder || ""
    };

    const response = await this.request(apiConfig.endpoints.getClientCampaignsList, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Get Client Campaigns List');
  }

  // Delete Client Campaign API
  async deleteClientCampaign(campaignId) {
    const response = await this.request(`${apiConfig.endpoints.deleteClientCampaign}?campaignId=${campaignId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    });

    return this.handleApiResponse(response, 'Delete Client Campaign');
  }

  // Update Client Campaign API
  async updateClientCampaign(campaignID, campaignName) {
    const payload = {
      campaignID: campaignID,
      campaignName: campaignName
    };

    const response = await this.request(apiConfig.endpoints.updateClientCampaign, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Update Client Campaign');
  }

  // Create Sub Campaign API
  async createSubCampaign(campaignName) {
    const payload = {
      campaignName: campaignName
    };

    const response = await this.request(apiConfig.endpoints.createSubCampaign, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Create Sub Campaign');
  }

  // Get Sub Campaigns List API
  async getSubCampaignsList(params = {}) {
    const payload = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      searchTerm: params.searchTerm || "",
      type: params.type || "",
      sortBy: params.sortBy || "",
      sortOrder: params.sortOrder || ""
    };

    const response = await this.request(apiConfig.endpoints.getSubCampaignsList, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Get Sub Campaigns List');
  }

  // Delete Sub Campaign API
  async deleteSubCampaign(campaignId) {
    const response = await this.request(`${apiConfig.endpoints.deleteSubCampaign}?campaignId=${campaignId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    });

    return this.handleApiResponse(response, 'Delete Sub Campaign');
  }

  // Update Sub Campaign API
  async updateSubCampaign(campaignID, campaignName) {
    const payload = {
      campaignID: campaignID,
      campaignName: campaignName
    };

    const response = await this.request(apiConfig.endpoints.updateSubCampaign, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });

    return this.handleApiResponse(response, 'Update Sub Campaign');
  }

  // Get Client Campaigns (for dropdown)
  async getClientCampaigns() {
    const response = await this.request(apiConfig.endpoints.getClientCampaigns, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Client Campaigns');
  }

  // Get Sub Campaigns (for dropdown)
  async getSubCampaigns() {
    const response = await this.request(apiConfig.endpoints.getSubCampaigns, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Sub Campaigns');
  }

  // Get Banner Categories (for dropdown)
  async getBannerCategories() {
    const response = await this.request(apiConfig.endpoints.getBannerCategories, {
      method: 'GET'
    });

    return this.handleApiResponse(response, 'Get Banner Categories');
  }

  // Handle token expiration
  handleTokenExpiration() {
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('affiliateCode');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userData');
    localStorage.removeItem('userPermissions');
    
    // Show a brief message to user (optional)
    // Session expired
    
    // Redirect to login page
    window.location.href = '/login';
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
